import * as Form from "@radix-ui/react-form";
import * as Switch from "@radix-ui/react-switch";

import {
  clearAuthError,
  selectAuthError,
  selectIsAuth,
  selectPreferredAuthMethod,
  selectIsFirstTime2FA,
  selectQrCodeUrl,
  selectSetupKey,
  selectRemember2FADays,
  setPreferredAuthMethod,
  selectRequires2FA,
  selectUsername,
  verify2FA,
  send2FACodeEmail,
  send2FARecoveryEmail,
  verify2FAResetCode,
} from "@/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { FormField } from "./form-field";
import Logo from "./Logo";
import { useNavigate } from "react-router-dom";

/**
 * Groups a base32 TOTP secret into 4-character blocks so it can be read off a screen
 * and typed without losing your place. Authenticator apps ignore the spaces; the copy
 * button still copies the raw, unspaced secret.
 */
const formatSetupKey = (key: string): string =>
  key.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();

/**
 * Clipboard fallback for non-secure contexts, where `navigator.clipboard` is undefined
 * (the same constraint that rules out `crypto.randomUUID` elsewhere in this app).
 * `execCommand` is deprecated but remains the only option there.
 *
 * @returns Whether the copy was accepted by the browser.
 */
const copyViaTextarea = (text: string): boolean => {
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(el);
  }
};

/**
 * TwoFactorAuth component renders the 2FA verification form.
 * Displays QR code for first-time setup or just code input for returning users.
 *
 * @component
 * @returns {React.ReactElement} The 2FA verification page.
 */
const TwoFactorAuth = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [useEmailAuth, setUseEmailAuth] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [recoveryCodeSent, setRecoveryCodeSent] = useState(false);
  const [emailSentOnLoad, setEmailSentOnLoad] = useState(false);
  const [copiedSetupKey, setCopiedSetupKey] = useState(false);
  
  const authError = useAppSelector(selectAuthError) as string | null;
  const isAuth = useAppSelector(selectIsAuth);
  const requires2FA = useAppSelector(selectRequires2FA);
  const username = useAppSelector(selectUsername);
  const preferredAuthMethod = useAppSelector(selectPreferredAuthMethod);
  const qrCodeUrl = useAppSelector(selectQrCodeUrl);
  const setupKey = useAppSelector(selectSetupKey);
  const isFirstTime2FA = useAppSelector(selectIsFirstTime2FA);
  const remember2FADays = useAppSelector(selectRemember2FADays);

  useEffect(() => {
    if (
      requires2FA &&
      !isFirstTime2FA &&
      preferredAuthMethod === "email" &&
      username &&
      !emailSentOnLoad
    ) {
      // Automatically send email for subsequent logins if preferred method is email
      dispatch(send2FACodeEmail());
      setUseEmailAuth(true);
      setEmailCodeSent(true);
      setEmailSentOnLoad(true); // prevent re-sending on re-renders
    }
  }, [
    requires2FA,
    isFirstTime2FA,
    preferredAuthMethod,
    username,
    dispatch,
    emailSentOnLoad,
  ]);

  // Revert the copy button after a moment, so a second copy still reads as feedback.
  useEffect(() => {
    if (!copiedSetupKey) {
      return;
    }
    const timer = setTimeout(() => setCopiedSetupKey(false), 2000);
    return () => clearTimeout(timer);
  }, [copiedSetupKey]);

  // Redirect to /chat if authenticated
  useEffect(() => {
    if (isAuth) {
      navigate("/chat", { replace: true });
    }
  }, [isAuth, navigate]);

  // Redirect to /login if 2FA is not required
  useEffect(() => {
    if (!requires2FA) {
      navigate("/login", { replace: true });
    }
  }, [requires2FA, navigate]);

  /**
   * Copies the raw (unspaced) TOTP secret so it can be pasted into an authenticator app.
   * On failure the button simply doesn't confirm - the key stays selectable on screen,
   * so the user can always copy it by hand.
   */
  const handleCopySetupKey = async (): Promise<void> => {
    if (!setupKey) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(setupKey);
        setCopiedSetupKey(true);
      } else {
        setCopiedSetupKey(copyViaTextarea(setupKey));
      }
    } catch {
      setCopiedSetupKey(false);
    }
  };

  /**
   * Handles the 2FA verification form submission.
   *
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
   */
  const handle2FASubmit: React.FormEventHandler<HTMLFormElement> = async (
    event
  ) => {
    event.preventDefault();

    if (!username) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const codeValue = formData.get("code") as string;

    // Save trust device preference to localStorage
    localStorage.setItem('trustDevice', rememberDevice.toString());

    if (isRecoveryMode) {
      // Verify reset code for recovery flow
      await dispatch(verify2FAResetCode({
        code: codeValue,
        trustDevice: rememberDevice,
      }));
    } else {
      // Standard 2FA verification
      const verifyParams = {
        username,
        code: codeValue,
        trustDevice: rememberDevice,
        initialEmail: isFirstTime2FA && useEmailAuth,
        useEmailAuth,
      };
      const result = await dispatch(verify2FA(verifyParams));

      if (verify2FA.fulfilled.match(result)) {
        if (isFirstTime2FA) {
          const method = useEmailAuth ? 'email' : 'authenticator';
          dispatch(setPreferredAuthMethod(method));
        }
      }
    }
  };

  /**
   * Handles sending a 2FA code via email.
   */
  const handleSendEmailCode = async () => {
    if (!username) {
      return;
    }
    
    await dispatch(send2FACodeEmail());
    
    setEmailCodeSent(true);
    setUseEmailAuth(true);
  };

  /**
   * Handles initiating recovery mode (lost authenticator).
   */
  const handleStartRecovery = async () => {
    if (!username) {
      return;
    }
    
    // Send recovery email
    await dispatch(send2FARecoveryEmail());
    
    setIsRecoveryMode(true);
    setRecoveryCodeSent(true);
    setCode("");
    dispatch(clearAuthError());
  };

  /**
   * Re-sends the code for whichever flow is on screen. The router deletes the outstanding row
   * before issuing a new one (remove2faTempSecret), which also resets the attempt budget — so
   * this is the only way out of a lockout, and the only thing the lockout message can point at.
   */
  const handleResendCode = async () => {
    if (!username) {
      return;
    }

    setCode("");
    dispatch(clearAuthError());
    if (isRecoveryMode) {
      await dispatch(send2FARecoveryEmail());
    } else {
      await dispatch(send2FACodeEmail());
    }
  };

  /**
   * Handles switching back from recovery mode.
   */
  const handleBackFromRecovery = () => {
    setIsRecoveryMode(false);
    setRecoveryCodeSent(false);
    setCode("");
    dispatch(clearAuthError());
  };

  /**
   * Handles switching back to authenticator app.
   */
  const handleBackToAuthenticator = () => {
    setUseEmailAuth(false);
    setEmailCodeSent(false);
    setCode("");
    dispatch(clearAuthError());
  };

  return (
    <div className="flex items-center justify-center h-dvh bg-neutral-100 text-neutral-600">
      <div className="w-full max-w-md p-6 bg-white border rounded border-neutral-300">
        <div className="flex flex-col gap-2">
          <Logo />
          <h2 className="text-2xl font-bold">Two-Factor Authentication</h2>
          
          {isRecoveryMode && recoveryCodeSent ? (
            <div className="flex flex-col gap-3 my-4">
              <p className="text-sm text-neutral-600">
                <strong>Account Recovery:</strong> We've sent a recovery code to your email address on file.
              </p>
              <p className="text-sm text-neutral-500">
                Enter the code below to regain access to your account.
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium text-left"
              >
                Send a new recovery code
              </button>
              <button
                type="button"
                onClick={handleBackFromRecovery}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium text-left"
              >
                Back to using authenticator app
              </button>
            </div>
          ) : isFirstTime2FA && qrCodeUrl && !useEmailAuth ? (
            <div className="flex flex-col gap-3 my-4">
              <p className="text-sm text-neutral-600">
                <strong>First-time setup:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="flex justify-center p-4 bg-white border rounded">
                <img 
                  src={qrCodeUrl} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              {setupKey && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-neutral-600">
                    If your authenticator app is on the same device, enter this key instead.
                  </p>
                  <div className="flex items-stretch gap-2">
                    <code
                      data-testid="2fa-setup-key"
                      className="flex-1 select-all break-all rounded border bg-neutral-50 px-3 py-2 font-mono text-sm tracking-wider text-neutral-800"
                    >
                      {formatSetupKey(setupKey)}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCopySetupKey}
                      aria-label="Copy setup key"
                      className="shrink-0"
                    >
                      {copiedSetupKey ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-sm text-neutral-500">
                After scanning or entering the key, enter the 6-digit code from your app below.
              </p>
              <button
                type="button"
                onClick={handleSendEmailCode}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium text-left"
              >
                Send verification code via email
              </button>
            </div>
          ) : useEmailAuth && emailCodeSent ? (
            <div className="flex flex-col gap-3 my-4">
              <p className="text-sm text-neutral-600">
                <strong>Email verification:</strong> We've sent a 6-digit code to your email address on file.
              </p>
              <p className="text-sm text-neutral-500">
                Enter the code below to complete {isFirstTime2FA ? 'setup' : 'verification'}.
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium text-left"
              >
                Send a new code
              </button>
              {isFirstTime2FA && (
                <button
                  type="button"
                  onClick={handleBackToAuthenticator}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium text-left"
                >
                  Back to using authenticator app
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-neutral-500">
              Enter the 6-digit verification code from your authenticator app
            </p>
          )}
          
          {authError && <p className="text-lg text-red-600">{authError}</p>}
        </div>

        <Form.Root onSubmit={handle2FASubmit} className="mt-4">
          <FormField
            name="code"
            label="Verification Code"
            value={code}
            // Deliberately does NOT clear the error. Every verify outcome overwrites
            // state.error anyway (fulfilled nulls it, rejected replaces it), so clearing per
            // keystroke only hid the message while the user was retyping — and hid it for good
            // once the router started returning terminal errors like an exhausted code budget,
            // which no amount of editing can make stale. Flow changes clear it explicitly.
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCode(e.target.value);
            }}
            error={authError}
          />

          {remember2FADays > 0 && (
            <div className="flex items-center justify-between mt-4">
              <label htmlFor="remember-device" className="text-sm font-medium cursor-pointer">
                Remember this device for {remember2FADays} {remember2FADays === 1 ? 'day' : 'days'}
              </label>
              <Switch.Root
                id="remember-device"
                checked={rememberDevice}
                onCheckedChange={setRememberDevice}
                className="w-11 h-6 bg-neutral-300 rounded-full relative data-[state=checked]:bg-blue-600 transition-colors"
              >
                <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform translate-x-0.5 data-[state=checked]:translate-x-[22px]" />
              </Switch.Root>
            </div>
          )}

          {!isFirstTime2FA && !useEmailAuth && !isRecoveryMode && (
            <button
              type="button"
              onClick={handleStartRecovery}
              className="text-sm text-orange-600 hover:text-orange-700 hover:underline font-medium text-left mt-2"
            >
              Lost your authenticator?
            </button>
          )}

          <Form.Submit asChild>
            <Button type="submit" className="w-full mt-4" disabled={code.length === 0}>
              Verify
            </Button>
          </Form.Submit>
        </Form.Root>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
